import { Component, h, State, Prop, Element, Listen, Watch, Host } from '@stencil/core';
import { IHeader, ISearch } from '../utils/interfaces/header.interface';
import { fetchNavigationData, fetchMockNavigationData } from '../utils/services/navigation.service';

@Component({
  tag: 'visa-navigation',
  styleUrl: 'visa-navigation.scss',
  shadow: false,
})
export class VisaNavigation {
  @Element() el!: HTMLElement;

  /**
   * The header content
   */
  @Prop() content?: any;

  /**
   * Use mock data for development
   */
  @Prop() useMockData: boolean = false;

  /**
   * Custom URL to fetch navigation data from
   */
  @Prop() dataUrl?: string;

  /**
   * Position type for the header (blended or separate)
   * blended = position absolute (default)
   * separate = position relative
   */
  @Prop() headerPosition: 'blended' | 'separate' = 'blended';

  /**
   * Show sign in button
   */
  @Prop() showSignIn: boolean = false;

  /**
   * Search configuration (can be passed separately for prop-based approach)
   */
  //IMP: Need to fix any here
  @Prop() searchConfig?: ISearch | any;

  /**
   * Internal state for header data
   */
  @State() headerData: IHeader;

  /**
   * Internal state for search data
   */
  //IMP: Need to fix any here
  @State() searchData: ISearch | any;

  /**
   * Mobile view state
   */
  @State() isMobile: boolean = false;

  /**
   * Navigation list open state
   */
  @State() navListOpen: boolean = false;

  /**
   * Search open state
   */
  @State() searchOpen: boolean = false;

  /**
   * Active tab index
   */
  @State() activeTabIndex: number = -1;

  /**
   * Active submenu index
   */
  @State() activeSubmenuIndex: number = -1; // Set to -1 to keep submenu closed by default

  /**
   * Active submenu heading index
   */
  @State() activeSubmenuHeadingIndex: number = -1;

  /**
   * Track active nested submenus
   */
  @State() activeNestedMenus: { [key: string]: boolean } = {};

  /**
   * Track navigation hierarchy for hierarchical color system
   * Format: { level: number, path: string[] }
   */
  @State() navigationHierarchy: { level: number; path: string[] } = { level: 0, path: [] };

  /**
   * Mobile menu state
   */
  @State() mobileMenuOpen: boolean = false;

  /**
   * Mobile menu navigation history for back button functionality
   */
  @State() mobileMenuHistory: Array<{
    title: string;
    level: number;
    parentIndex?: number;
    items: any[];
    cardData?: any;
  }> = [];

  /**
   * Current mobile menu card data
   */
  @State() currentMobileCardData: any = null;

  /**
   * Current mobile menu level
   */
  @State() currentMobileMenuLevel: number = 0;

  /**
   * Current mobile menu title
   */
  @State() currentMobileMenuTitle: string = '';

  /**
   * Current mobile menu items
   */
  @State() currentMobileMenuItems: any[] = [];

  /**
   * Play/pause button state
   */
  @State() isPaused: boolean = true;

  /**
   * Theme state (light or dark)
   */
  @Prop({ mutable: true }) theme: 'light' | 'dark' = 'dark'; // Default to dark theme

  /**
   * Store the original theme before menu opens
   */
  originalTheme: 'light' | 'dark' = 'dark';

  /**
   * Store the original scroll position
   */
  private scrollPosition: number = 0;

  /**
   * Track if header should be sticky
   */
  @State() isSticky: boolean = false;

  /**
   * Keyboard navigation states
   */
  @State() keyboardNavigationActive: boolean = false;
  @State() focusedSubmenuHeadingIndex: number = -1;
  @State() focusedSubmenuContentIndex: number = -1;
  @State() focusedNestedItemPath: string = '';

  /**
   * Helper function to determine if an item should be highlighted (active/selected)
   * For L1 (left column): Check if it's the currently selected heading
   * For L2 (right column): Check if it has an expanded nested menu
   * For nested items: Check if they are currently expanded
   */
  private isItemActive(itemPath: string, level: number, index?: number): boolean {
    if (level === 1) {
      // L1 items (left column headings) - active if it's the selected heading
      return this.activeSubmenuHeadingIndex === index;
    } else if (level === 2) {
      // L2 items (right column) - active if they have expanded nested menus
      return this.activeNestedMenus[itemPath] === true;
    } else {
      // Nested items - active if they are expanded
      return this.activeNestedMenus[itemPath] === true;
    }
  }

  /**
   * Helper function to determine if any item at the same level is expanded
   * This helps us know when to apply grey color to non-active items
   */
  private hasAnyExpandedAtLevel(level: number): boolean {
    if (level === 1) {
      // For L1, always return true when submenu is open (so non-active items turn grey)
      return this.activeSubmenuIndex !== -1;
    } else if (level === 2) {
      // For L2, check if any nested menus are expanded
      return Object.keys(this.activeNestedMenus).length > 0;
    } else {
      // For nested levels, check if any items at this level are expanded
      return Object.keys(this.activeNestedMenus).some(path => this.activeNestedMenus[path]);
    }
  }

  /**
   * Disable page scrolling
   */
  private disableScroll() {
    // Store current scroll position
    this.scrollPosition = window.pageYOffset;

    // Add the no-scroll class to the body
    document.body.classList.add('no-scroll');

    // For sticky headers, we need to handle scroll prevention differently
    // to avoid interfering with the fixed positioning
    if (this.isSticky) {
      // When header is sticky, just prevent scrolling with overflow hidden
      // and maintain the current scroll position
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
    } else {
      // Original behavior for non-sticky headers
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
    }
  }

  /**
   * Enable page scrolling
   */
  private enableScroll() {
    // Remove the no-scroll class from the body
    document.body.classList.remove('no-scroll');

    if (this.isSticky) {
      // For sticky headers, restore overflow and padding
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    } else {
      // Original behavior for non-sticky headers
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';

      // Restore scroll position
      window.scrollTo(0, this.scrollPosition);
    }
  }

  /**
   * Get scrollbar width to prevent layout shift when hiding scrollbar
   */
  private getScrollbarWidth(): number {
    // Create a temporary div to measure scrollbar width
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    // Type assertion for IE-specific property
    (outer.style as any).msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  /**
   * Component lifecycle method
   * Called once when the component is first connected to the DOM
   */
  async componentWillLoad() {
    // Check if we're on mobile
    this.isMobile = window.innerWidth < 1024;

    // Apply theme class to host element
    if (this.theme === 'dark') {
      this.el.classList.add('visa-navigation-theme-dark');
    } else {
      this.el.classList.remove('visa-navigation-theme-dark');
    }

    // Add direct window resize listener for better responsiveness
    this.addResizeListener();

    // Add keyboard event listener for escape key
    this.addKeyboardListener();

    // Load navigation data and search configuration
    await this.loadNavigationData();

    // Don't select any tab by default
    this.activeTabIndex = -1;
    this.activeSubmenuIndex = -1;

    // Store original theme before changing
    this.originalTheme = this.theme;

    // No need to add menu-open class by default since submenu is closed
  }

  componentDidLoad() {
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Component lifecycle method
   * Called when the component is removed from the DOM
   */
  disconnectedCallback() {
    // Clean up event listeners
    this.removeResizeListener();
    this.removeKeyboardListener();
  }

  /**
   * Listen for window resize events
   */
  @Listen('window_resized', { target: 'document' })
  handleResize() {
    // Update mobile state
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 1024;

    // If mobile state changed, force a re-render
    if (wasMobile !== this.isMobile) {
      // Force a re-render by updating a state property
      this.activeNestedMenus = { ...this.activeNestedMenus };
    }

    // If we're not on mobile anymore and the nav list is open, close it
    if (!this.isMobile && this.navListOpen) {
      this.closeNavList();
    }

    // If we're not on mobile anymore and the mobile menu is open, close it
    if (!this.isMobile && this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  /**
   * Add direct window resize listener
   */
  private addResizeListener() {
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('scroll', this.onWindowScroll);
  }

  /**
   * Remove direct window resize listener
   */
  private removeResizeListener() {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('scroll', this.onWindowScroll);
  }

  /**
   * Add keyboard event listener
   */
  private addKeyboardListener() {
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('click', this.onDocumentClick);
  }

  /**
   * Remove keyboard event listener
   */
  private removeKeyboardListener() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('click', this.onDocumentClick);
  }

  /**
   * Handle keyboard events
   */
  private onKeyDown = (event: KeyboardEvent) => {
    // Handle escape key
    if (event.key === 'Escape') {
      if (this.searchOpen) {
        this.toggleSearch();
        event.preventDefault();
      } else if (this.activeSubmenuIndex !== -1) {
        this.closeActiveSubmenu();
        event.preventDefault();
      } else if (this.mobileMenuOpen) {
        this.closeMobileMenu();
        event.preventDefault();
      }
    }
  };

  /**
   * Handle keyboard navigation for submenu items
   */
  private handleSubmenuKeyDown = (
    event: KeyboardEvent,
    type: 'heading' | 'content' | 'nested',
    index?: number,
    path?: string
  ) => {
    // Activate keyboard navigation mode
    this.keyboardNavigationActive = true;

    switch (event.key) {
      case 'Enter':
      case ' ': // Space key
        event.preventDefault();
        this.handleKeyboardActivation(type, index, path);
        break;
      case 'Escape':
        event.preventDefault();
        this.closeActiveSubmenu();
        break;
      case 'Tab':
        // Let Tab work naturally, but track that we're using keyboard
        this.keyboardNavigationActive = true;
        break;
    }
  };

  /**
   * Handle keyboard activation of menu items
   */
  private handleKeyboardActivation = (
    type: 'heading' | 'content' | 'nested',
    index?: number,
    path?: string
  ) => {
    if (type === 'heading' && index !== undefined) {
      this.setActiveSubmenuHeading(index);
      this.setSubmenuHeadingFocus(index);

      // Immediately focus the first item in the expanded content
      setTimeout(() => {
        this.focusFirstExpandedContent();
      }, 50); // Small delay to ensure DOM is updated
    } else if (type === 'nested' && path) {
      // Create a synthetic event for nested menu toggle
      const syntheticEvent = new Event('click') as any;
      this.toggleNestedMenu(path, syntheticEvent);
    }
    // For content items, they are typically links and will be handled by their href
  };

  /**
   * Set keyboard focus on submenu heading
   */
  private setSubmenuHeadingFocus = (index: number) => {
    this.focusedSubmenuHeadingIndex = index;
    this.focusedSubmenuContentIndex = -1;
    this.focusedNestedItemPath = '';
  };

  /**
   * Set keyboard focus on submenu content
   */
  private setSubmenuContentFocus = (index: number) => {
    this.focusedSubmenuContentIndex = index;
    this.focusedSubmenuHeadingIndex = -1;
    this.focusedNestedItemPath = '';
  };

  /**
   * Set keyboard focus on nested item
   */
  private setNestedItemFocus = (path: string) => {
    this.focusedNestedItemPath = path;
    this.focusedSubmenuHeadingIndex = -1;
    this.focusedSubmenuContentIndex = -1;
  };

  /**
   * Clear all keyboard focus states
   */
  private clearAllKeyboardFocus = () => {
    this.focusedSubmenuHeadingIndex = -1;
    this.focusedSubmenuContentIndex = -1;
    this.focusedNestedItemPath = '';
    this.keyboardNavigationActive = false;
  };

  /**
   * Focus the first item in the expanded content area
   */
  private focusFirstExpandedContent = () => {
    if (this.activeSubmenuIndex === -1 || this.activeSubmenuHeadingIndex === -1) {
      return;
    }

    // Find the first focusable element in the expanded content area
    const submenuElement = this.el.querySelector(`#submenu-${this.activeSubmenuIndex}`);
    if (!submenuElement) {
      return;
    }

    // Look for the first focusable element in the content column (column02)
    const contentColumn = submenuElement.querySelector('.visa-navigation-column02');
    if (!contentColumn) {
      return;
    }

    // Find the first link or button with tabindex="0"
    const firstFocusableElement = contentColumn.querySelector(
      'a[tabindex="0"], button[tabindex="0"]'
    ) as HTMLElement;
    if (firstFocusableElement) {
      firstFocusableElement.focus();
      // Set the content focus state to match
      this.setSubmenuContentFocus(0);
    }
  };

  /**
   * Handle document click events to close navigation when clicking on links
   */
  private onDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // Check if the clicked element is a navigation link (but not a toggle/expand link)
    if (target && target.closest) {
      const clickedLink = target.closest('a[href]:not([href="#"])');
      const isWithinNavigation = target.closest('visa-navigation');

      // If a real link was clicked within the navigation component, close the menu
      if (clickedLink && isWithinNavigation && this.activeSubmenuIndex !== -1) {
        // Don't close if it's a toggle link (href="#") or if it has preventDefault behavior
        const href = clickedLink.getAttribute('href');
        if (href && href !== '#' && !href.startsWith('javascript:')) {
          // Add a small delay to allow the link to be processed before closing
          setTimeout(() => {
            this.closeActiveSubmenu();
          }, 100);
        }
      }
    }
  };

  /**
   * Window resize handler
   */
  private onWindowResize = () => {
    // Update mobile state
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 1024;

    // If mobile state changed, force a re-render
    if (wasMobile !== this.isMobile) {
      // Force a re-render by updating a state property
      this.activeNestedMenus = { ...this.activeNestedMenus };
    }
  };

  /**
   * Window scroll handler
   */
  private onWindowScroll = () => {
    const scrollY = window.scrollY;
    const shouldBeSticky = scrollY > 64;

    if (shouldBeSticky !== this.isSticky) {
      this.isSticky = shouldBeSticky;
    }
  };

  /**
   * Toggle the theme between light and dark
   */
  private toggleTheme = () => {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    if (this.theme === 'dark') {
      this.el.classList.add('visa-navigation-theme-dark');
    } else {
      this.el.classList.remove('visa-navigation-theme-dark');
    }
  };

  /**
   * Handle search form submission with absolute URL construction
   */
  private handleSearchSubmit = (event: Event) => {
    event.preventDefault();

    if (!this.searchData) {
      return;
    }

    const form = event.target as HTMLFormElement;
    const formData = new (window as any).FormData(form);
    const searchQuery = formData.get(this.searchData.queryParam || 'q') as string;

    if (!searchQuery || !searchQuery.trim()) {
      return;
    }

    // Construct absolute URL
    let searchUrl = this.searchData.url;

    // If URL is relative (starts with /), make it absolute using current origin
    if (searchUrl.startsWith('/')) {
      searchUrl = window.location.origin + searchUrl;
    }

    // Create URL object to properly handle query parameters
    const url = new (window as any).URL(searchUrl);

    // Add the search query parameter
    url.searchParams.set(this.searchData.queryParam || 'q', searchQuery.trim());
    // Navigate to the search URL
    window.location.href = url.toString();
  };

  /**
   * Toggle play/pause state and emit events to control videos
   */
  private togglePlayPause = () => {
    this.isPaused = !this.isPaused;

    // Create and dispatch the appropriate custom event
    const eventName = this.isPaused ? 'globalPauseEvent' : 'globalPlayEvent';
    const customEvent = new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
      detail: { timestamp: Date.now() },
    });

    // Dispatch the event from the component's element
    this.el.dispatchEvent(customEvent);

    // If pausing, find and pause all video elements in the document
    if (!this.isPaused) {
      this.pauseAllVideos();
    } else {
      this.playNonUserPausedVideos();
    }
  };

  /**
   * Find and pause all video elements in the document
   * Store their current playing state to know which ones to resume later
   */
  private pauseAllVideos() {
    // Find all video elements in the document
    const videoElements = [
      ...document.querySelectorAll('video'), // light DOM videos
    ];

    document.querySelectorAll('vsr-video').forEach(el => {
      if (el.shadowRoot) {
        const vid = el.shadowRoot.querySelector('video');
        if (vid) videoElements.push(vid);
      }
    });

    // Store the current state of each video before pausing
    videoElements.forEach(video => {
      // Mark if the video was already paused by the user
      // We'll use this to determine which videos to play when resuming
      if (!video.paused || video.readyState > 2) {
        // Store that this video was playing before global pause
        video.dataset.wasPlaying = 'true';
        // Pause the video
        video.pause();
      } else {
        // Video was already paused by user
        video.dataset.wasPlaying = 'false';
      }
    });

    // ---- Pause CSS animations ----
    const animatedElements = document.querySelectorAll(
      '.headerReveal1, .headerReveal2, .subHeaderReveal, .fadeReveal, .blockReveal, .cardFade, .cardFlip'
    );

    animatedElements.forEach(el => {
      (el as HTMLElement).style.animationPlayState = 'paused';
    });
  }

  /**
   * Play only videos that were playing before the global pause
   */
  private playNonUserPausedVideos() {
    // Find all video elements in the document
    const videoElements = [
      ...document.querySelectorAll('video'), // light DOM videos
    ];

    document.querySelectorAll('vsr-video').forEach(el => {
      if (el.shadowRoot) {
        const vid = el.shadowRoot.querySelector('video');
        if (vid) videoElements.push(vid);
      }
    });

    // Only play videos that were playing before the global pause
    videoElements.forEach(video => {
      // Check if the video was playing before the global pause
      if (video.dataset.wasPlaying === 'true') {
        // Try to play the video
        const playPromise = video.play();

        // Handle the play promise to catch any errors
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn('Auto-play was prevented:', error);
          });
        }

        // Clear the wasPlaying flag
        delete video.dataset.wasPlaying;
      }
    });

    // Resume CSS animations
    const animatedElements = document.querySelectorAll(
      '.headerReveal1, .headerReveal2, .subHeaderReveal, .fadeReveal, .blockReveal, .cardFade, .cardFlip'
    );

    animatedElements.forEach(el => {
      (el as HTMLElement).style.animationPlayState = 'running';
    });
  }

  /**
   * Load navigation data and search configuration
   */
  private async loadNavigationData() {
    try {
      // Parse searchConfig if it's a JSON string (backward-compatible)
      let parsedSearchConfig = this.searchConfig;
      if (typeof this.searchConfig === 'string' && this.searchConfig.trim()) {
        try {
          parsedSearchConfig = JSON.parse(this.searchConfig);
        } catch (error) {
          console.warn('Failed to parse searchConfig JSON string, using as-is:', error);
          // Fall back to original value if parsing fails
          parsedSearchConfig = this.searchConfig;
        }
      }

      if (this.content) {
        // Prop-based approach
        let parsedData;
        if (typeof this.content === 'string') {
          parsedData = JSON.parse(this.content);
        } else {
          parsedData = this.content;
        }

        // Set header data
        this.headerData = parsedData.header || parsedData || {};

        // Handle search configuration priority:
        // 1. searchConfig prop (highest priority) - now properly parsed
        // 2. content.navSearch (from prop data)
        // 3. headerData.navSearch (from header data)
        this.searchData = parsedSearchConfig || parsedData.navSearch || this.headerData.navSearch;
      } else {
        // API-driven approach
        let navigationData;

        if (this.useMockData) {
          navigationData = await fetchMockNavigationData(this.dataUrl);
        } else {
          navigationData = await fetchNavigationData(this.dataUrl);
        }

        // Set header data
        this.headerData = navigationData.header || navigationData || {};

        // Parse navSearch from API if it's a JSON string
        let parsedNavSearch = navigationData.navSearch;
        if (typeof navigationData.navSearch === 'string' && navigationData.navSearch.trim()) {
          try {
            parsedNavSearch = JSON.parse(navigationData.navSearch);
          } catch (error) {
            console.warn('Failed to parse navSearch JSON string from API, using as-is:', error);
            parsedNavSearch = navigationData.navSearch;
          }
        }
        // Handle search configuration priority:
        // 1. searchConfig prop (highest priority) - now properly parsed
        // 2. navigationData.navSearch (from API root level) - now properly parsed
        // 3. navigationData.header.navSearch (from API header level) - now properly parsed
        this.searchData = parsedSearchConfig || parsedNavSearch;
      }
    } catch (error) {
      console.error('Failed to load navigation data:', error);
    }
  }

  /**
   * Watch for changes to the header data
   */
  @Watch('data')
  handleDataChange(newValue: IHeader) {
    if (newValue) {
      this.headerData = newValue;
    }
  }

  /**
   * Watch for changes to the search config
   */
  @Watch('searchConfig')
  handleSearchConfigChange(newValue: ISearch | string) {
    if (newValue) {
      // Parse searchConfig if it's a JSON string (backward-compatible)
      let parsedSearchConfig = newValue;
      if (typeof newValue === 'string' && newValue.trim()) {
        try {
          parsedSearchConfig = JSON.parse(newValue);
        } catch (error) {
          console.warn('Failed to parse searchConfig JSON string in watcher, using as-is:', error);
          // Fall back to original value if parsing fails
          parsedSearchConfig = newValue;
        }
      }
      this.searchData = parsedSearchConfig;
    }
  }

  /**
   * Watch for changes to the content prop
   */
  @Watch('content')
  async handleContentChange() {
    await this.loadNavigationData();
  }

  /**
   * Toggle the navigation list
   */
  toggleNavList = () => {
    if (this.navListOpen) {
      this.closeNavList();
    } else {
      this.openNavList();
    }
  };

  /**
   * Toggle the mobile menu
   */
  toggleMobileMenu = () => {
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  };

  /**
   * Open the mobile menu
   */
  private openMobileMenu() {
    // Store original theme before changing
    this.originalTheme = this.theme;

    // Reset mobile menu state
    this.mobileMenuHistory = [];
    this.currentMobileMenuLevel = 0;
    this.currentMobileMenuTitle = '';

    // Set initial menu items from lowernav
    if (this.headerData?.lowernav && this.headerData.lowernav.length > 0) {
      this.currentMobileMenuItems = this.headerData.lowernav.map((item, originalIndex) => ({
        text: item.menuItem?.text,
        description: item.menuItem?.description,
        hasChildren: item.cols && item.cols.length > 0,
        data: item,
        originalIndex: originalIndex, // Preserve the original index from lowernav
      }));
    }

    // Open the menu
    this.mobileMenuOpen = true;
    this.disableScroll();

    // Add class to body to indicate mobile menu is open
    document.body.classList.add('mobile-menu-open');
  }

  /**
   * Close the mobile menu
   */
  private closeMobileMenu = () => {
    this.mobileMenuOpen = false;
    this.enableScroll();

    // Remove class from body
    document.body.classList.remove('mobile-menu-open');
  };

  /**
   * Navigate to a submenu in mobile view
   */
  private goToMobileSubmenu = (item: any, index: number) => {
    // If the item doesn't have children, it's a link - handle accordingly
    if (!item.hasChildren) {
      // If it's a link item with a link property, navigate to it
      if (item.link) {
        window.location.href = item.link;
        return;
      }
      return;
    }

    // Store current menu in history for back button
    this.mobileMenuHistory.push({
      title: this.currentMobileMenuTitle,
      level: this.currentMobileMenuLevel,
      items: [...this.currentMobileMenuItems],
      cardData: this.currentMobileCardData,
    });

    // Update current menu level and title
    this.currentMobileMenuLevel++;
    this.currentMobileMenuTitle = item.text;

    // Prepare new menu items based on the selected item
    let newMenuItems = [];
    let cardData = null;

    if (item.data && item.data.cols) {
      // Extract all submenu headings from all columns
      const headings = [];
      item.data.cols.forEach(column => {
        if (column && column.subMenu) {
          column.subMenu.forEach(subMenu => {
            if (subMenu && subMenu.label) {
              headings.push({
                text: subMenu.label,
                description: subMenu.description, // Add description for L1 items
                hasChildren: subMenu.nav && subMenu.nav.length > 0,
                data: subMenu,
              });
            }
          });
        }
      });
      newMenuItems = headings;

      // FIXED: Get card data based on the selected menu index
      // Each menu should show its own card data, not just the first one found
      let targetSubmenu = null;

      // Find the submenu that corresponds to the selected menu index
      let submenuIndex = 0;
      item.data.cols.forEach(column => {
        if (column && column.subMenu) {
          column.subMenu.forEach(subMenu => {
            if (subMenu && subMenu.label) {
              if (submenuIndex === index) {
                targetSubmenu = subMenu;
              }
              submenuIndex++;
            }
          });
        }
      });

      // STRICT CHECK: If we found the target submenu, use its card data ONLY if it has sharedCardImage
      if (targetSubmenu && targetSubmenu.sharedCardImage && targetSubmenu.sharedCardImage.image) {
        cardData = {
          image: targetSubmenu.sharedCardImage.image,
          link: targetSubmenu.sharedCardImage.link,
          label: targetSubmenu.label,
        };
        cardData = null; // Explicitly set to null
      }
      // NO FALLBACK: Don't show any card if the specific menu doesn't have sharedCardImage
      // This ensures menus like "Find an ATM" that don't have sharedCardImage show no card
    }

    // Update current menu items and card data
    this.currentMobileMenuItems = newMenuItems;
    this.currentMobileCardData = cardData; // This will be null for menus without sharedCardImage
  };

  /**
   * Navigate to any level submenu in mobile view (generic function)
   */
  private goToMobileNestedLevel = (item: any, _index: number) => {
    // If the item doesn't have children, it's a link - handle accordingly
    if (!item.hasChildren) {
      // If it's a link item with a link property, navigate to it
      if (item.link) {
        window.location.href = item.link;
        return;
      }
      return;
    }

    // Store current menu in history for back button
    this.mobileMenuHistory.push({
      title: this.currentMobileMenuTitle,
      level: this.currentMobileMenuLevel,
      items: [...this.currentMobileMenuItems],
      cardData: this.currentMobileCardData,
    });

    // Update current menu level and title
    this.currentMobileMenuLevel++;
    this.currentMobileMenuTitle = item.text;

    // Prepare new menu items based on the selected item
    let newMenuItems = [];
    let cardData = null;

    // Handle different data structures
    if (item.data) {
      // PRIORITY: Check for featureCards first
      if (item.data.featureCards && item.data.featureCards.length > 0) {
        // Flatten featureCards into menu items
        item.data.featureCards.forEach(featureCard => {
          // Add the feature card title as a section header (non-clickable)
          newMenuItems.push({
            text: featureCard.eyebrowText,
            description: '',
            hasChildren: false,
            isHeader: true, // Mark as header
            data: { text: featureCard.eyebrowText },
          });

          // Add each feature card data item (simple links only, no nested functionality)
          featureCard.data.forEach(dataItem => {
            newMenuItems.push({
              text: dataItem.text,
              description: dataItem.description,
              hasChildren: false, // FeatureCards items are always simple links
              link: dataItem.link,
              target: dataItem.target,
              data: dataItem,
            });
          });
        });
      }
      // FALLBACK: For L2+ levels, check for nav array
      else if (item.data.nav) {
        newMenuItems = item.data.nav.map(navItem => ({
          text: navItem.text,
          description: navItem.description,
          hasChildren:
            (navItem.subMenu && navItem.subMenu.length > 0) ||
            (navItem.subItems && navItem.subItems.length > 0) ||
            (navItem.nav && navItem.nav.length > 0),
          link: navItem.link,
          target: navItem.target,
          data: navItem,
        }));
      }
      // For deeper nested levels, check for subMenu array
      else if (item.data.subMenu) {
        newMenuItems = item.data.subMenu.map(subItem => ({
          text: subItem.text,
          description: subItem.description,
          hasChildren:
            (subItem.subMenu && subItem.subMenu.length > 0) ||
            (subItem.nav && subItem.nav.length > 0),
          link: subItem.link,
          target: subItem.target,
          data: subItem,
        }));
      }

      // IMPROVED: Check if this item has card data, if not, inherit from parent ONLY for level 3+
      if (item.data.sharedCardImage || item.data.image) {
        cardData = {
          image: item.data.sharedCardImage ? item.data.sharedCardImage.image : item.data.image,
          link: item.data.sharedCardImage ? item.data.sharedCardImage.link : item.data.link,
          label: item.data.sharedCardImage?.link?.text || item.data.label,
        };
      } else if (this.currentMobileCardData && this.currentMobileMenuLevel >= 3) {
        // LEVEL 3+ FIX: Only inherit from parent for level 3 and deeper
        // Level 2 menus without sharedCardImage should show NO card
        cardData = this.currentMobileCardData;
      } else {
        // For level 2 menus without sharedCardImage, explicitly set to null
        cardData = null;
      }
    }

    // Update current menu items and card data
    this.currentMobileMenuItems = newMenuItems;
    this.currentMobileCardData = cardData;
  };

  /**
   * Go back to previous menu level
   */
  private goBackMobileMenu = () => {
    if (this.mobileMenuHistory.length === 0) {
      this.closeMobileMenu();
      return;
    }

    // Get the last menu from history
    const previousMenu = this.mobileMenuHistory.pop();

    // Restore previous menu state
    this.currentMobileMenuLevel = previousMenu.level;
    this.currentMobileMenuTitle = previousMenu.title;
    this.currentMobileMenuItems = previousMenu.items;
    this.currentMobileCardData = previousMenu.cardData;
  };

  /**
   * Open the navigation list
   */
  private openNavList() {
    this.navListOpen = true;
    document.body.classList.add('body');

    if (this.isMobile) {
      const main = document.getElementsByTagName('main')[0];
      const footer = document.getElementsByTagName('footer')[0];

      if (main) main.classList.add('vs-d-none');
      if (footer) footer.classList.add('vs-d-none');
    }
  }

  /**
   * Close the navigation list
   */
  private closeNavList() {
    this.navListOpen = false;
    document.body.classList.remove('body');

    const main = document.getElementsByTagName('main')[0];
    const footer = document.getElementsByTagName('footer')[0];

    if (main) {
      main.classList.remove('vs-d-none');
    }

    if (footer) footer.classList.remove('vs-d-none');
  }

  /**
   * Toggle the search
   */
  private toggleSearch = () => {
    const wasOpen = this.searchOpen;
    this.searchOpen = !this.searchOpen;

    if (this.searchOpen) {
      // Store original theme before changing
      this.originalTheme = this.theme;

      // Add menu-open class to navigation
      const navElement = this.el.querySelector('.visa-navigation-navigation');
      if (navElement) {
        navElement.classList.add('visa-navigation-menu-open');
      }

      // Prevent page scrolling
      this.disableScroll();
    } else if (wasOpen) {
      // Remove menu-open class from navigation
      const navElement = this.el.querySelector('.visa-navigation-navigation');
      if (navElement) {
        navElement.classList.remove('visa-navigation-menu-open');
      }

      // Restore page scrolling
      this.enableScroll();
    }
  };

  private setActiveTab = (index: number) => {
    this.activeTabIndex = index;
    this.activeSubmenuIndex = index;
    this.originalTheme = this.theme;
    const navElement = this.el.querySelector('.visa-navigation-navigation');
    if (navElement) {
      navElement.classList.add('visa-navigation-menu-open');
    }

    this.disableScroll();
  };

  private onTabLeave = () => {
    // Do nothing - keep submenu open when leaving individual tabs
    // Only close when leaving the entire navigation area
  };

  private onTabHover = (index: number) => {
    // Only show submenu, don't change active tab
    this.activeSubmenuIndex = index;

    // Don't reset the heading index when switching between tabs
    // Keep the current heading selection so content remains visible
    // Only reset when explicitly closing or leaving all tabs

    // Optional: add CSS class for open menu
    const navElement = this.el.querySelector('.visa-navigation-navigation');
    if (navElement) {
      navElement.classList.add('visa-navigation-menu-open');
      this.disableScroll();
    }
  };

  /**
   * Handle hover over tab content specifically to reset submenu heading
   */
  private onTabContentHover = () => {
    // Reset the heading index when hovering over the tab content area
    this.activeSubmenuHeadingIndex = -1;
  };

  /**
   * Handle when user leaves the entire navigation area
   */
  private onNavigationLeave = () => {
    // Reset all navigation states when leaving the entire navigation area
    this.activeSubmenuIndex = -1;
    this.activeSubmenuHeadingIndex = -1;

    // Remove CSS class
    const navElement = this.el.querySelector('.visa-navigation-navigation');
    if (navElement) {
      navElement.classList.remove('visa-navigation-menu-open');
    }

    // Restore page scrolling
    this.enableScroll();
  };

  /**
   * Handle hover over the gap area between left menus and right secondary nav (desktop only)
   */
  private onHoverZoneEnter = () => {
    // Safety check - only work on desktop
    if (this.isMobile) return;

    // Activate the first menu (index 0) when hovering over the gap
    this.onTabHover(0);

    // Also trigger the visual hover state on the first tab
    const firstTab = this.el.querySelector(
      '.visa-navigation-tab-container:first-child .visa-navigation-tab'
    );
    if (firstTab) {
      firstTab.classList.add('visa-navigation-hover-zone-active');
    }
  };

  /**
   * Handle leaving the hover zone (desktop only)
   */
  private onHoverZoneLeave = () => {
    // Safety check - only work on desktop
    if (this.isMobile) return;

    // Remove the visual hover state from the first tab
    const firstTab = this.el.querySelector(
      '.visa-navigation-tab-container:first-child .visa-navigation-tab'
    );
    if (firstTab) {
      firstTab.classList.remove('visa-navigation-hover-zone-active');
    }

    // The onNavigationLeave will handle cleanup when user leaves the entire nav area
    // No specific action needed here as the submenu should stay open until user leaves nav entirely
  };

  /**
   * Set active submenu heading
   */
  private closeActiveSubmenu = () => {
    // Close the submenu
    this.activeSubmenuIndex = -1;
    // Also reset the active tab index to remove bold styling
    this.activeTabIndex = -1;
    // Reset the submenu heading index so no content shows by default
    this.activeSubmenuHeadingIndex = -1;

    // Clear all nested menus
    this.activeNestedMenus = {};

    // Clear all keyboard focus states
    this.clearAllKeyboardFocus();

    // Remove menu-open class from navigation
    const navElement = this.el.querySelector('.visa-navigation-navigation');
    if (navElement) {
      navElement.classList.remove('visa-navigation-menu-open');
    }

    // Restore page scrolling
    this.enableScroll();
  };

  /**
   * Set active submenu heading
   */
  private setActiveSubmenuHeading = (index: number) => {
    this.activeSubmenuHeadingIndex = index;
  };

  /**
   * Toggle a nested submenu
   */
  private toggleNestedMenu = (path: string, event: Event) => {
    // event.preventDefault();
    // event.stopPropagation();
    console.log(event);
    const isCurrentlyOpen = this.activeNestedMenus[path];
    const newActiveNestedMenus = { ...this.activeNestedMenus };

    if (!isCurrentlyOpen) {
      // Opening a menu - close all sibling menus at the same level
      const pathParts = path.split('-');
      const currentLevel = pathParts.length;

      // Close all menus at the same level or deeper
      Object.keys(newActiveNestedMenus).forEach(menuPath => {
        const menuPathParts = menuPath.split('-');
        const menuLevel = menuPathParts.length;

        // If it's at the same level or deeper, and shares the same parent path
        if (menuLevel >= currentLevel) {
          // Check if they share the same parent path (all parts except the last one)
          const currentParentPath = pathParts.slice(0, -1).join('-');
          const menuParentPath = menuPathParts.slice(0, currentLevel - 1).join('-');

          if (currentParentPath === menuParentPath && menuPath !== path) {
            // Close sibling menus and their children
            delete newActiveNestedMenus[menuPath];
          } else if (menuLevel > currentLevel && menuPath.startsWith(path + '-')) {
            // Close any deeper nested menus under other siblings
            if (
              Object.keys(newActiveNestedMenus).some(
                activePath =>
                  activePath !== path &&
                  activePath.length < menuPath.length &&
                  menuPath.startsWith(activePath + '-') &&
                  activePath.split('-').length === currentLevel
              )
            ) {
              delete newActiveNestedMenus[menuPath];
            }
          }
        }
      });

      // Open the clicked menu
      newActiveNestedMenus[path] = true;
    } else {
      // Closing a menu - close it and all its children
      Object.keys(newActiveNestedMenus).forEach(menuPath => {
        if (menuPath === path || menuPath.startsWith(path + '-')) {
          delete newActiveNestedMenus[menuPath];
        }
      });
    }

    this.activeNestedMenus = newActiveNestedMenus;
  };

  /**
   * Render submenu headings (left column)
   */
  private renderSubmenuHeadings(item: any, itemIndex: number) {
    if (!item || !item.cols || item.cols.length === 0) {
      return null;
    }

    // Extract all submenu headings from all columns
    const headings = [];
    item.cols.forEach(column => {
      if (column && column.subMenu) {
        column.subMenu.forEach(subMenu => {
          if (subMenu && subMenu.label) {
            headings.push(subMenu);
          }
        });
      }
    });

    return (
      <div class="visa-navigation-column visa-navigation-column01">
        {headings.map((heading, index) => {
          const isActive = this.isItemActive(`heading-${index}`, 1, index);
          const hasAnyExpanded = this.hasAnyExpandedAtLevel(1);
          const isKeyboardFocused = this.focusedSubmenuHeadingIndex === index;

          return (
            <div
              class={{
                'visa-navigation-nav-list-item': true,
                'visa-navigation-active': this.activeSubmenuHeadingIndex === index,
                'visa-navigation-nav-item-active': isActive,
                'visa-navigation-nav-item-default': !isActive && !hasAnyExpanded,
                'visa-navigation-nav-item-parent': !isActive && hasAnyExpanded,
                'visa-navigation-keyboard-focused': isKeyboardFocused,
              }}
              key={`heading-${itemIndex}-${index}`}
              onClick={() => this.setActiveSubmenuHeading(index)}
              tabindex={this.activeSubmenuIndex === itemIndex ? '0' : '-1'}
              onKeyDown={e => this.handleSubmenuKeyDown(e, 'heading', index)}
              onFocus={() => this.setSubmenuHeadingFocus(index)}
              role="menuitem"
              aria-expanded={this.activeSubmenuHeadingIndex === index ? 'true' : 'false'}
            >
              <div class="analytics-button visa-navigation-container visa-navigation-heading-container">
                {heading.label}
              </div>
              <div class="visa-navigation-slot">
                <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor" />
                </svg>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /**
   * Render nested navigation items recursively
   */
  private renderNestedItems(items: any[], parentPath: string, level: number) {
    if (!items || items.length === 0) {
      return null;
    }

    return (
      <div class={`visa-navigation-nested-menu visa-navigation-nested-level-${level}`}>
        {items.map((item, index) => {
          const itemPath = `${parentPath}-nested-${index}`;
          const hasSubMenu = item.subMenu && item.subMenu.length > 0;
          const isActive = this.activeNestedMenus[itemPath] === true;
          const opensInNewWindow = item.target === 'new';
          const isKeyboardFocused =
            this.focusedNestedItemPath === itemPath && this.keyboardNavigationActive;

          // Apply hierarchical color system for nested items
          const hasAnyExpandedAtThisLevel = Object.keys(this.activeNestedMenus).some(
            path =>
              path.startsWith(parentPath) && path !== parentPath && this.activeNestedMenus[path]
          );

          return (
            <div
              class={{
                'visa-navigation-nav-list-item': true,
                'visa-navigation-nav-item-active': isActive,
                'visa-navigation-nav-item-default': !isActive && !hasAnyExpandedAtThisLevel,
                'visa-navigation-nav-item-parent': !isActive && hasAnyExpandedAtThisLevel,
                'visa-navigation-keyboard-focused': isKeyboardFocused,
              }}
              key={itemPath}
            >
              {hasSubMenu ? (
                <a
                  href="#"
                  onClick={e => this.toggleNestedMenu(itemPath, e)}
                  onKeyDown={e => this.handleSubmenuKeyDown(e, 'nested', undefined, itemPath)}
                  class="analytics-col03 analytics-button visa-navigation-container"
                  aria-expanded={isActive ? 'true' : 'false'}
                  tabindex={this.activeSubmenuIndex !== -1 ? '0' : '-1'}
                  onFocus={() => this.setNestedItemFocus(itemPath)}
                  role="menuitem"
                >
                  {item.mediaType && item.imagePath && (
                    <span
                      class={`visa-navigation-menu-icon visa-navigation-menu-icon-${item.mediaType}`}
                    >
                      <img src={item.imagePath} alt="" width="40" height="40" />
                    </span>
                  )}
                  {item.text}
                </a>
              ) : (
                <a
                  href={item.link}
                  target={opensInNewWindow ? '_blank' : '_self'}
                  class="analytics-button visa-navigation-container"
                  tabindex={this.activeSubmenuIndex !== -1 ? '0' : '-1'}
                  onFocus={() => this.setNestedItemFocus(itemPath)}
                  role="menuitem"
                >
                  {item.mediaType && item.imagePath && (
                    <span
                      class={`visa-navigation-menu-icon visa-navigation-menu-icon-${item.mediaType}`}
                    >
                      <img src={item.imagePath} alt="" width="40" height="40" />
                    </span>
                  )}
                  {item.text}
                  {opensInNewWindow && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: '4px' }}
                    >
                      <path
                        d="M4.55487 11.9985L4 11.4436L10.6608 4.78399H4.94372V4H11.9996V11.0559H11.2156V5.33886L4.55487 11.9985Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </a>
              )}
              {hasSubMenu && (
                <div class="visa-navigation-slot">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
                      fill="currentColor"
                      class={{
                        'rotate-down': isActive,
                      }}
                    />
                  </svg>
                </div>
              )}
              {hasSubMenu && isActive && this.renderNestedItems(item.subMenu, itemPath, level + 1)}
            </div>
          );
        })}
      </div>
    );
  }

  /**
   * Render submenu content (right column)
   */
  private renderSubmenuContent(item: any, itemIndex: number) {
    if (!item || !item.cols || item.cols.length === 0) {
      return null;
    }

    // Extract all submenu headings from all columns
    const headings = [];
    item.cols.forEach(column => {
      if (column && column.subMenu) {
        column.subMenu.forEach(subMenu => {
          if (subMenu && subMenu.label) {
            headings.push(subMenu);
          }
        });
      }
    });

    // Get the active heading
    const activeHeading = headings[this.activeSubmenuHeadingIndex];
    if (!activeHeading) {
      return null;
    }

    // PRIORITY: Check if activeHeading has featureCards - if so, ONLY render featureCards (ignore nav array)
    if (activeHeading.featureCards && activeHeading.featureCards.length > 0) {
      return (
        <div class="visa-navigation-column visa-navigation-column02">
          <div class="visa-navigation-feature-cards-container">
            {activeHeading.featureCards.map((featureCard, cardIndex) => (
              <div class="visa-navigation-feature-card-column" key={`feature-card-${cardIndex}`}>
                <h4 class="visa-navigation-feature-card-heading">{featureCard.eyebrowText}</h4>
                <div class="visa-navigation-feature-card-items">
                  {featureCard.data.map((navItem, navItemIndex) => {
                    const itemPath = `featurecard-${itemIndex}-${cardIndex}-${navItemIndex}`;
                    const isKeyboardFocused =
                      this.focusedSubmenuContentIndex === navItemIndex &&
                      this.keyboardNavigationActive;

                    return (
                      <div
                        class={{
                          'visa-navigation-nav-list-item': true,
                          'is-icon-desc': navItem?.description || navItem?.imagePath ? true : false,
                          'visa-navigation-keyboard-focused': isKeyboardFocused,
                        }}
                        key={itemPath}
                      >
                        <a
                          href={navItem.link}
                          target={navItem.target === 'new' ? '_blank' : '_self'}
                          class={`analytics-button visa-navigation-container ${navItem.target === 'new' ? 'newTab' : ''}`}
                          tabindex={this.activeSubmenuIndex === itemIndex ? '0' : '-1'}
                          onFocus={() => this.setSubmenuContentFocus(navItemIndex)}
                          role="menuitem"
                        >
                          {navItem.mediaType && navItem.imagePath && (
                            <span
                              class={`visa-navigation-menu-icon visa-navigation-menu-icon-${navItem.mediaType}`}
                            >
                              <img src={navItem.imagePath} alt="" width="40" height="40" />
                            </span>
                          )}
                          <div class="visa-navigation-nav-item-content">
                            <div class="visa-navigation-nav-item-text">{navItem.text}</div>
                            {navItem.description && (
                              <div class="visa-navigation-nav-item-description">
                                {navItem.description}
                              </div>
                            )}
                          </div>
                          {navItem.target === 'new' && (
                            <span class="visa-navigation-external-icon">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ marginLeft: '4px' }}
                              >
                                <path
                                  d="M4.55487 11.9985L4 11.4436L10.6608 4.78399H4.94372V4H11.9996V11.0559H11.2156V5.33886L4.55487 11.9985Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </span>
                          )}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div class={`visa-navigation-column visa-navigation-column02`}>
        {activeHeading.nav &&
          activeHeading.nav.map((navItem, navItemIndex) => {
            const hasSubMenu = navItem.subMenu && navItem.subMenu.length > 0;
            const itemPath = `navitem-${itemIndex}-${navItemIndex}`;
            const isActive = this.isItemActive(itemPath, 2);
            const hasAnyExpanded = this.hasAnyExpandedAtLevel(2);
            const isKeyboardFocused =
              this.focusedSubmenuContentIndex === navItemIndex && this.keyboardNavigationActive;

            return (
              <div
                class={{
                  'visa-navigation-nav-list-item': true,
                  'is-icon-desc': navItem?.description || navItem?.imagePath ? true : false,
                  'visa-navigation-nav-item-active': isActive,
                  'visa-navigation-nav-item-default': !isActive && !hasAnyExpanded,
                  'visa-navigation-nav-item-parent': !isActive && hasAnyExpanded,
                  'visa-navigation-keyboard-focused': isKeyboardFocused,
                }}
                key={itemPath}
              >
                {hasSubMenu ? (
                  <a
                    href="#"
                    onClick={e => this.toggleNestedMenu(itemPath, e)}
                    onKeyDown={e => this.handleSubmenuKeyDown(e, 'nested', undefined, itemPath)}
                    class={`analytics-col02 analytics-button visa-navigation-container ${this.activeNestedMenus[itemPath] ? 'is_active' : ''}`}
                    aria-expanded={this.activeNestedMenus[itemPath] ? 'true' : 'false'}
                    tabindex={this.activeSubmenuIndex === itemIndex ? '0' : '-1'}
                    onFocus={() => this.setSubmenuContentFocus(navItemIndex)}
                    role="menuitem"
                  >
                    {navItem.mediaType && navItem.imagePath && (
                      <span
                        class={`visa-navigation-menu-icon visa-navigation-menu-icon-${navItem.mediaType}`}
                      >
                        <img src={navItem.imagePath} alt={navItem.text} width="40" height="40" />
                      </span>
                    )}
                    <div class="visa-navigation-nav-item-content">
                      <div class="visa-navigation-nav-item-text">{navItem.text}</div>
                      {navItem.description && (
                        <div class="visa-navigation-nav-item-description">
                          {navItem.description}
                        </div>
                      )}
                    </div>
                  </a>
                ) : (
                  <a
                    href={navItem.link}
                    target={navItem.target === 'new' ? '_blank' : '_self'}
                    class={`analytics-button visa-navigation-container ${navItem.target === 'new' ? 'newTab' : ''}`}
                    tabindex={this.activeSubmenuIndex === itemIndex ? '0' : '-1'}
                    onFocus={() => this.setSubmenuContentFocus(navItemIndex)}
                    role="menuitem"
                  >
                    {navItem.mediaType && navItem.imagePath && (
                      <span
                        class={`visa-navigation-menu-icon visa-navigation-menu-icon-${navItem.mediaType}`}
                      >
                        <img src={navItem.imagePath} alt="" width="40" height="40" />
                      </span>
                    )}
                    <div class="visa-navigation-nav-item-content">
                      <div class="visa-navigation-nav-item-text">{navItem.text}</div>
                      {navItem.description && (
                        <div class="visa-navigation-nav-item-description">
                          {navItem.description}
                        </div>
                      )}
                    </div>
                    {navItem.target === 'new' && (
                      <span class="visa-navigation-external-icon">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ marginLeft: '4px' }}
                        >
                          <path
                            d="M4.55487 11.9985L4 11.4436L10.6608 4.78399H4.94372V4H11.9996V11.0559H11.2156V5.33886L4.55487 11.9985Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                    )}
                  </a>
                )}
                {hasSubMenu && (
                  <div class="visa-navigation-slot">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
                        fill="currentColor"
                        class={{
                          'rotate-down': this.activeNestedMenus[itemPath],
                        }}
                      />
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  }

  private renderNestedItemsColumn(item: any, itemIndex: number) {
    if (!item || !item.cols || item.cols.length === 0) {
      return null;
    }

    // Extract all submenu headings from all columns
    const headings = [];
    item.cols.forEach(column => {
      if (column && column.subMenu) {
        column.subMenu.forEach(subMenu => {
          if (subMenu && subMenu.label) {
            headings.push(subMenu);
          }
        });
      }
    });

    // Get the active heading
    const activeHeading = headings[this.activeSubmenuHeadingIndex];
    if (!activeHeading || !activeHeading.nav) {
      return null;
    }

    // Find items with active nested menus
    const nestedItemsToRender = [];

    activeHeading.nav.forEach((navItem, navItemIndex) => {
      const hasSubMenu = navItem.subMenu && navItem.subMenu.length > 0;
      const itemPath = `navitem-${itemIndex}-${navItemIndex}`;

      if (hasSubMenu && this.activeNestedMenus[itemPath]) {
        nestedItemsToRender.push({
          subMenu: navItem.subMenu,
          itemPath: itemPath,
          level: 1,
        });
      }
    });

    // Only render the column if there are nested items to show
    if (nestedItemsToRender.length === 0) {
      return null;
    }

    return (
      <div class="visa-navigation-column visa-navigation-column03">
        {nestedItemsToRender.map(nestedItem =>
          this.renderNestedItems(nestedItem.subMenu, nestedItem.itemPath, nestedItem.level)
        )}
      </div>
    );
  }

  /**
   * Render card column (right column)
   */
  private renderCardColumn(item: any) {
    if (!item || !item.cols || item.cols.length === 0) {
      return null;
    }

    // Extract all submenu headings from all columns
    const headings = [];
    item.cols.forEach(column => {
      if (column && column.subMenu) {
        column.subMenu.forEach(subMenu => {
          if (subMenu && subMenu.label) {
            headings.push(subMenu);
          }
        });
      }
    });

    // Get the active heading
    const activeHeading = headings[this.activeSubmenuHeadingIndex];
    if (!activeHeading) {
      return null;
    }

    // Check if the active heading has a shared card image

    if (activeHeading.sharedCardImage && activeHeading.sharedCardImage.image) {
      const { image, link } = activeHeading.sharedCardImage;
      if (link?.link) {
        return (
          <a
            href={link?.link}
            target={`${link.target == 'same' ? '_self' : '_blank'}`}
            class="analytics-button visa-navigation-column visa-navigation-card-column03 visa-navigation-card-link"
          >
            <div class="visa-navigation-card">
              <div class="visa-navigation-card-container">
                <div
                  class="visa-navigation-card-image"
                  style={{ backgroundImage: `url(${image.src})` }}
                ></div>
              </div>
              <div class="visa-navigation-copy-container">
                <div class="visa-navigation-title">{link?.text || activeHeading.label}</div>
              </div>
            </div>
          </a>
        );
      }

      return (
        <div class="visa-navigation-column visa-navigation-card-column03">
          <div class="visa-navigation-card">
            <div class="visa-navigation-card-container">
              <div
                class="visa-navigation-card-image"
                style={{ backgroundImage: `url(${image.src})` }}
              ></div>
            </div>
            <div class="visa-navigation-copy-container">
              <div class="visa-navigation-title">{link?.text || activeHeading.label}</div>
            </div>
          </div>
        </div>
      );
    }
    // If no shared image in active heading, fall back to finding a column with an image
    let imageSubMenu = null;
    item.cols.forEach(column => {
      if (column && column.subMenu) {
        column.subMenu.forEach(subMenu => {
          if (subMenu && subMenu.image && subMenu.link) {
            imageSubMenu = subMenu;
          }
        });
      }
    });

    if (!imageSubMenu) {
      return null;
    }

    return (
      <div class="visa-navigation-column visa-navigation-card-column03">
        <div class="visa-navigation-card">
          <div class="visa-navigation-card-container">
            <div
              class="visa-navigation-card-image"
              style={{ backgroundImage: `url(${imageSubMenu.image.src})` }}
            ></div>
          </div>
          <div class="visa-navigation-copy-container">
            <div class="visa-navigation-title">{imageSubMenu.link.text}</div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render the logo
   */
  private renderLogo() {
    // Create the SVG logo element
    const svgElement = (
      <svg
        width="60"
        height="20"
        viewBox="0 0 1336 430"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Visa Home"
      >
        <path d="M507.369 7.60031L332.588 423.495H218.557L132.547 91.592C127.325 71.1489 122.785 63.6595 106.904 55.0468C80.9894 41.0181 38.172 27.8632 0.5 19.6942L3.05875 7.60031H186.614C210.012 7.60031 231.045 23.1338 236.357 50.0053L281.782 290.663L394.047 7.60031H507.369ZM954.17 287.709C954.629 177.942 801.98 171.895 803.03 122.86C803.356 107.937 817.603 92.0705 848.788 88.0207C864.245 86.0028 906.833 84.4633 955.136 106.633L974.083 18.4391C948.127 9.0427 914.732 0 873.18 0C766.554 0 691.515 56.5308 690.883 137.478C690.194 197.351 744.443 230.762 785.313 250.658C827.359 271.031 841.466 284.124 841.307 302.348C841.008 330.246 807.772 342.562 776.712 343.047C722.492 343.879 691.029 328.415 665.949 316.786L646.397 407.899C671.602 419.432 718.125 429.494 766.359 430C879.688 430 953.822 374.17 954.17 287.709ZM1235.73 423.502H1335.5L1248.41 7.60031H1156.32C1135.62 7.60031 1118.15 19.6249 1110.42 38.1125L948.545 423.495H1061.82L1084.31 361.368H1222.71L1235.74 423.495L1235.73 423.502ZM1115.36 276.135L1172.14 119.982L1204.82 276.135H1115.37H1115.36ZM661.506 7.60031L572.304 423.495H464.433L553.67 7.60031H661.506Z" />
      </svg>
    );

    // If there's header data with logos
    if (this.headerData?.logos && this.headerData.logos.length > 0) {
      // Use the first logo from the data
      const logo = this.headerData.logos[0];
      const { link } = logo;

      // If there's a link, wrap the SVG in an anchor tag
      if (link && link.link) {
        const target = link.target === '_new' ? '_blank' : '_self';
        const rel = link.linkType === 'external' ? 'noopener noreferrer' : null;

        return (
          <div class="visa-navigation-logo">
            <a
              class="visa-navigation-logo-link"
              href={link.link}
              target={target}
              rel={rel}
              aria-label={link.ally_label || 'Visa Home'}
            >
              {svgElement}
            </a>
          </div>
        );
      }
    }

    // Otherwise, just return the SVG
    return <div class="visa-navigation-logo">{svgElement}</div>;
  }

  /**
   * Render top-level tabs
   */
  private renderTopLevelTabs() {
    // Use all items from lowernav if available
    if (this.headerData?.lowernav && this.headerData.lowernav.length > 0) {
      return (
        <div class="visa-navigation-top-level-tabs" onMouseLeave={() => this.onNavigationLeave()}>
          {this.headerData.lowernav.map((item, index) => {
            return (
              <div
                class="visa-navigation-tab-container"
                onMouseOver={() => this.onTabHover(index)}
                onMouseOut={() => this.onTabLeave()}
                onClick={() => this.setActiveTab(index)}
              >
                <button
                  class={{
                    'visa-navigation-tab': true,
                    'visa-navigation-active': this.activeSubmenuIndex === index,
                  }}
                  aria-expanded="false"
                  aria-controls={`submenu-${index}`}
                  aria-label={item?.menuItem?.ally_label || item?.menuItem?.text}
                >
                  <div
                    class="visa-navigation-tab-content"
                    onMouseOver={() => this.onTabContentHover()}
                  >
                    <div class="visa-navigation-tab-text">{item.menuItem?.text}</div>
                    {item.menuItem?.description && (
                      <div class="visa-navigation-tab-description">
                        {item.menuItem?.description}
                      </div>
                    )}
                  </div>
                </button>

                {item.cols && item.cols.length > 0 && (
                  <div
                    class={{
                      'visa-navigation-submenu': true,
                      'visa-navigation-active': this.activeSubmenuIndex === index,
                    }}
                    id={`submenu-${index}`}
                  >
                    <div class="visa-navigation-menu">
                      <div class="visa-navigation-text-link">
                        {item.menuItem?.headerText || item.menuItem?.link ? (
                          <a
                            href={item.menuItem?.link || '#'}
                            target={item.menuItem?.target === 'new' ? '_blank' : '_self'}
                            class="visa-navigation-link-wrapper analytics-button"
                          >
                            <span class="visa-navigation-link">
                              {item.menuItem?.headerText || `Explore ${item.menuItem?.text}`}
                            </span>
                            <span class="visa-navigation-arrow-icon">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"
                                  fill="currentColor"
                                />
                              </svg>
                            </span>
                          </a>
                        ) : (
                          <div class="visa-navigation-link-wrapper analytics-button">
                            <span class="visa-navigation-link">
                              {item.menuItem?.headerText || `Explore ${item.menuItem?.text}`}
                            </span>
                            <span class="visa-navigation-arrow-icon">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"
                                  fill="currentColor"
                                />
                              </svg>
                            </span>
                          </div>
                        )}
                      </div>

                      <div class="visa-navigation-menu-container">
                        <div class="visa-navigation-row">
                          {this.renderSubmenuHeadings(item, index)}
                          {this.renderSubmenuContent(item, index)}
                          {this.renderNestedItemsColumn(item, index)}
                        </div>
                        {this.renderCardColumn(item)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Fallback to default tabs
    return (
      <div class="visa-navigation-top-level-tabs">
        <button
          class={{
            'visa-navigation-tab': true,
            'visa-navigation-active': this.activeTabIndex === 0,
          }}
          onClick={() => this.setActiveTab(0)}
        >
          Personal
        </button>
        <button
          class={{
            tab: true,
            active: this.activeTabIndex === 1,
          }}
          onClick={() => this.setActiveTab(1)}
        >
          Business
        </button>
      </div>
    );
  }

  /**
   * Render secondary navigation
   */
  private renderSecondaryNav() {
    return (
      <div class="visa-navigation-secondary-nav">
        <div class="visa-navigation-icon-buttons">
          <button
            class={`visa-navigation-icon-button visa-navigation-toggle-theme ${this.theme == 'light' ? 'visa-navigation-light' : 'visa-navigation-dark'}`}
            style={{ display: 'none' }}
            aria-label="Toggle theme"
            onClick={this.toggleTheme}
          >
            {this.theme === 'dark' ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path
                  d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"
                  fill="#ffffff"
                />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path
                  d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"
                  fill="#161616"
                />
              </svg>
            )}
          </button>
          <button
            class={`visa-navigation-icon-button visa-navigation-play-pause-button ${this.isPaused ? 'pause' : 'play'}`}
            aria-label={this.isPaused ? 'Resume playback' : 'Pause playback'}
            onClick={this.togglePlayPause}
          >
            {this.isPaused ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_13715_1109)">
                  <path
                    d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15V16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16V15C11.866 15 15 11.866 15 8Z"
                    fill="#1A1A1A"
                  />
                  <path d="M7 10.6667H6V5.33333H7V10.6667Z" fill="#1A1A1A" />
                  <path d="M10.3333 10.6667H9.33333V5.33333H10.3333V10.6667Z" fill="#1A1A1A" />
                </g>
                <defs>
                  <clipPath id="clip0_13715_1109">
                    <rect width="16" height="16" fill="currentColor" />
                  </clipPath>
                </defs>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"
                  fill="#161616"
                />
              </svg>
            )}
          </button>
          {this.searchData && (
            <button
              class="visa-navigation-icon-button visa-navigation-search-button"
              aria-label="Search"
              onClick={this.toggleSearch}
            >
              <svg
                width="14"
                height="15"
                viewBox="0 0 14 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.9137 6.95705C11.9137 4.03525 9.54519 1.66669 6.62337 1.66669C3.70161 1.66675 1.33307 4.03529 1.33301 6.95705C1.33301 9.87886 3.70157 12.2474 6.62337 12.2474C9.54523 12.2474 11.9137 9.8789 11.9137 6.95705ZM12.9137 6.95705C12.9137 8.69843 12.2055 10.2738 11.0622 11.4128L13.8532 14.2038L13.1462 14.9108L10.2965 12.0612C9.2628 12.8064 7.99507 13.2474 6.62337 13.2474C3.14929 13.2474 0.333008 10.4311 0.333008 6.95705C0.333068 3.48301 3.14933 0.666751 6.62337 0.666687C10.0975 0.666687 12.9137 3.48297 12.9137 6.95705Z"
                  fill="#1A1A1A"
                />
              </svg>
            </button>
          )}
        </div>
        {/* Only show sign-in button when showSignIn prop is true */}
        {this.showSignIn && <button class="visa-navigation-sign-in-button">Sign in</button>}
      </div>
    );
  }

  /**
   * Render search overlay (responsive for desktop and mobile)
   */
  private renderSearchOverlay() {
    if (!this.searchOpen || !this.searchData) {
      return null;
    }

    if (this.isMobile) {
      return this.renderMobileSearchOverlay();
    } else {
      return this.renderDesktopSearchOverlay();
    }
  }

  /**
   * Render desktop search overlay (covers only header height)
   */
  private renderDesktopSearchOverlay() {
    return (
      <div
        class="visa-navigation-search-overlay visa-navigation-desktop"
        role="search"
        aria-label={this.searchData.label || 'Search'}
      >
        <div class="visa-navigation-search-container">
          <div class="visa-navigation-search-field">
            <div class="visa-navigation-search-content">
              <form
                class="visa-navigation-search-form"
                onSubmit={this.handleSearchSubmit}
                role="search"
              >
                <button
                  type="submit"
                  class="visa-navigation-search-icon"
                  aria-label={this.searchData.submit || 'Submit search'}
                  aria-hidden="true"
                  style={{ display: 'block' }}
                >
                  <svg
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.9137 6.95705C11.9137 4.03525 9.54519 1.66669 6.62337 1.66669C3.70161 1.66675 1.33307 4.03529 1.33301 6.95705C1.33301 9.87886 3.70157 12.2474 6.62337 12.2474C9.54523 12.2474 11.9137 9.8789 11.9137 6.95705ZM12.9137 6.95705C12.9137 8.69843 12.2055 10.2738 11.0622 11.4128L13.8532 14.2038L13.1462 14.9108L10.2965 12.0612C9.2628 12.8064 7.99507 13.2474 6.62337 13.2474C3.14929 13.2474 0.333008 10.4311 0.333008 6.95705C0.333068 3.48301 3.14933 0.666751 6.62337 0.666687C10.0975 0.666687 12.9137 3.48297 12.9137 6.95705Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <div class="visa-navigation-search-input-container">
                  <input
                    type="text"
                    class="visa-navigation-search-input"
                    placeholder={
                      this.searchData.placeholder || 'Search for credit cards, news, case studies'
                    }
                    name={this.searchData.queryParam || 'q'}
                    aria-label={this.searchData.label || 'Search'}
                    ref={el => el && this.searchOpen && el.focus()}
                  />
                  <button
                    type="submit"
                    class="visa-navigation-search-submit-button"
                    aria-label={this.searchData.submit || 'Submit search'}
                    style={{ display: 'none' }}
                  >
                    {this.searchData.submit || 'Search'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div class="visa-navigation-search-close">
            <button
              class="visa-navigation-search-close-button"
              onClick={this.toggleSearch}
              aria-label={this.searchData.cancel || 'Close search'}
            >
              <svg
                width="15"
                height="16"
                viewBox="0 0 15 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line x1="0.346217" y1="14.6859" x2="14.5878" y2="0.64396" stroke="currentColor" />
                <line x1="0.353553" y1="0.981373" x2="14.4957" y2="15.1235" stroke="currentColor" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render mobile search overlay (covers full screen)
   */
  private renderMobileSearchOverlay() {
    return (
      <div
        class="visa-navigation-search-overlay visa-navigation-mobile"
        role="search"
        aria-label={this.searchData.label || 'Search'}
      >
        <div class="visa-navigation-mobile-search-header">
          <button
            class="visa-navigation-mobile-search-back"
            onClick={this.toggleSearch}
            aria-label={this.searchData.cancel || 'Back'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
              />
            </svg>
          </button>
          <button
            class="visa-navigation-mobile-search-close"
            onClick={this.toggleSearch}
            aria-label={this.searchData.cancel || 'Close search'}
          >
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line x1="0.346217" y1="14.6859" x2="14.5878" y2="0.64396" stroke="currentColor" />
              <line x1="0.353553" y1="0.981373" x2="14.4957" y2="15.1235" stroke="currentColor" />
            </svg>
          </button>
        </div>
        <div class="visa-navigation-mobile-search-content">
          <div class="visa-navigation-search-container">
            <div class="visa-navigation-search-field">
              <div class="visa-navigation-search-content">
                {/* <div class="visa-navigation-search-icon" aria-hidden="true">
                  <svg
                    width="20"
                    height="21"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.9137 6.95705C11.9137 4.03525 9.54519 1.66669 6.62337 1.66669C3.70161 1.66675 1.33307 4.03529 1.33301 6.95705C1.33301 9.87886 3.70157 12.2474 6.62337 12.2474C9.54523 12.2474 11.9137 9.8789 11.9137 6.95705ZM12.9137 6.95705C12.9137 8.69843 12.2055 10.2738 11.0622 11.4128L13.8532 14.2038L13.1462 14.9108L10.2965 12.0612C9.2628 12.8064 7.99507 13.2474 6.62337 13.2474C3.14929 13.2474 0.333008 10.4311 0.333008 6.95705C0.333068 3.48301 3.14933 0.666751 6.62337 0.666687C10.0975 0.666687 12.9137 3.48297 12.9137 6.95705Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div class="visa-navigation-search-input-container">
                  <form action={this.searchData.url || '#'} method="get" role="search">
                    <input
                      type="text"
                      class="visa-navigation-search-input"
                      placeholder={
                        this.searchData.placeholder || 'Search for credit cards, news, case studies'
                      }
                      name={this.searchData.queryParam || 'q'}
                      aria-label={this.searchData.label || 'Search'}
                      ref={el => el && this.searchOpen && el.focus()}
                    />
                    <button
                      type="submit"
                      class="visa-navigation-search-submit-button"
                      aria-label={this.searchData.submit || 'Submit search'}
                      style={{ display: 'none' }}
                    >
                      {this.searchData.submit || 'Search'}
                    </button>
                  </form>
                </div> */}
                <form
                  class="visa-navigation-search-form"
                  onSubmit={this.handleSearchSubmit}
                  role="search"
                >
                  <button
                    type="submit"
                    class="visa-navigation-search-icon"
                    aria-label={this.searchData.submit || 'Submit search'}
                    aria-hidden="true"
                    style={{ display: 'block' }}
                  >
                    <svg
                      width="14"
                      height="15"
                      viewBox="0 0 14 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.9137 6.95705C11.9137 4.03525 9.54519 1.66669 6.62337 1.66669C3.70161 1.66675 1.33307 4.03529 1.33301 6.95705C1.33301 9.87886 3.70157 12.2474 6.62337 12.2474C9.54523 12.2474 11.9137 9.8789 11.9137 6.95705ZM12.9137 6.95705C12.9137 8.69843 12.2055 10.2738 11.0622 11.4128L13.8532 14.2038L13.1462 14.9108L10.2965 12.0612C9.2628 12.8064 7.99507 13.2474 6.62337 13.2474C3.14929 13.2474 0.333008 10.4311 0.333008 6.95705C0.333068 3.48301 3.14933 0.666751 6.62337 0.666687C10.0975 0.666687 12.9137 3.48297 12.9137 6.95705Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                  <div class="visa-navigation-search-input-container">
                    <input
                      type="text"
                      class="visa-navigation-search-input"
                      placeholder={
                        this.searchData.placeholder || 'Search for credit cards, news, case studies'
                      }
                      name={this.searchData.queryParam || 'q'}
                      aria-label={this.searchData.label || 'Search'}
                      ref={el => el && this.searchOpen && el.focus()}
                    />
                    <button
                      type="submit"
                      class="visa-navigation-search-submit-button"
                      aria-label={this.searchData.submit || 'Submit search'}
                      style={{ display: 'none' }}
                    >
                      {this.searchData.submit || 'Search'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render mobile card
   */
  private renderMobileCard() {
    if (!this.currentMobileCardData || !this.currentMobileCardData.image) {
      return null;
    }

    const { image, link, label } = this.currentMobileCardData;

    const imageUrl = image.src;
    const imageAlt = image.altText || label || 'Card image';
    const linkText = link?.text || label || '';
    const linkUrl = link?.link || '#';
    const linkTarget = link?.target === 'same' ? '_self' : '_blank';

    // If there's a valid link, wrap the entire card in a clickable link
    if (linkUrl && linkUrl !== '#') {
      return (
        <div class="visa-navigation-mobile-menu-card">
          <a
            href={linkUrl}
            target={linkTarget}
            class="visa-navigation-mobile-menu-card-link-wrapper"
          >
            <div class="visa-navigation-mobile-menu-card-container">
              <div
                class="visa-navigation-mobile-menu-card-image"
                style={{ backgroundImage: `url(${imageUrl})` }}
                role="img"
                aria-label={imageAlt}
              ></div>
            </div>
            {linkText && (
              <div class="visa-navigation-mobile-menu-card-title">
                <span class="visa-navigation-mobile-menu-card-text">{linkText}</span>
              </div>
            )}
          </a>
        </div>
      );
    }

    // Fallback for cards without links (maintain original structure)
    return (
      <div class="visa-navigation-mobile-menu-card">
        <div class="visa-navigation-mobile-menu-card-container">
          <div
            class="visa-navigation-mobile-menu-card-image"
            style={{ backgroundImage: `url(${imageUrl})` }}
            role="img"
            aria-label={imageAlt}
          ></div>
        </div>
        {linkText && (
          <div class="visa-navigation-mobile-menu-card-title">
            <span class="visa-navigation-mobile-menu-card-text">{linkText}</span>
          </div>
        )}
      </div>
    );
  }

  /**
   * Render mobile menu
   */
  private renderMobileMenu() {
    if (!this.mobileMenuOpen) {
      return null;
    }

    return (
      <div
        class={`visa-navigation-mobile-menu-overlay ${this.theme === 'dark' ? 'visa-navigation-theme-dark' : 'visa-navigation-theme-light'}`}
        data-level={this.currentMobileMenuLevel.toString()}
      >
        <div class="visa-navigation-mobile-menu">
          <div class="visa-navigation-mobile-menu-header">
            {this.currentMobileMenuLevel > 0 && (
              <button class="visa-navigation-mobile-menu-back" onClick={this.goBackMobileMenu}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                  <path
                    d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            )}
            <div class="visa-navigation-mobile-menu-title">{this.currentMobileMenuTitle}</div>
            <button class="visa-navigation-mobile-menu-close" onClick={this.closeMobileMenu}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
          {this.currentMobileMenuLevel === 1 && (
            <div class="visa-navigation-menu">
              <div class="visa-navigation-mobile-text-link">
                {this.headerData?.lowernav &&
                  this.headerData.lowernav.length > 0 &&
                  (() => {
                    // Find the current menu item based on the title
                    const currentMenuItem = this.headerData.lowernav.find(
                      item => item.menuItem?.text === this.currentMobileMenuTitle
                    );
                    const menuItem = currentMenuItem?.menuItem;

                    return menuItem?.headerText || menuItem?.link ? (
                      <a
                        href={menuItem?.link || '#'}
                        target={menuItem?.target === 'new' ? '_blank' : '_self'}
                        class="visa-navigation-mobile-link-wrapper"
                      >
                        <span class="visa-navigation-mobile-link">
                          {menuItem?.headerText || `Explore ${this.currentMobileMenuTitle}`}
                        </span>
                        <span class="visa-navigation-mobile-arrow-icon">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                      </a>
                    ) : (
                      <div class="visa-navigation-mobile-link-wrapper">
                        <span class="visa-navigation-mobile-link">
                          {menuItem?.headerText || `Explore ${this.currentMobileMenuTitle}`}
                        </span>
                        <span class="visa-navigation-mobile-arrow-icon">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                      </div>
                    );
                  })()}
              </div>
            </div>
          )}
          <div class="visa-navigation-mobile-menu-content">
            <ul class="visa-navigation-mobile-menu-list">
              {this.currentMobileMenuItems.map((item, index) => (
                <li
                  class={`visa-navigation-mobile-menu-item ${item.data.description || item.description || item.data.imagePath ? 'is-icon-desc' : ''}`}
                  key={`mobile-menu-item-${index}`}
                  onClick={() =>
                    this.currentMobileMenuLevel === 0
                      ? this.goToMobileSubmenu(
                          item,
                          item.originalIndex !== undefined ? item.originalIndex : index
                        )
                      : this.goToMobileNestedLevel(item, index)
                  }
                >
                  <div class="visa-navigation-mobile-menu-item-content">
                    {item.data.mediaType && item.data.imagePath && (
                      <span
                        class={`visa-navigation-menu-icon visa-navigation-menu-icon-${item.data.mediaType}`}
                      >
                        <img src={item.data.imagePath} alt="" width="40" height="40" />
                      </span>
                    )}
                    <div class="visa-navigation-mobile-menu-item-content_info">
                      <span
                        class={`visa-navigation-mobile-menu-item-text ${item.data.description || item.description ? 'is-icon-desc' : ''} ${this.currentMobileMenuLevel === 1 ? 'level-1' : ''} ${item.isHeader ? 'feature-card-title' : ''}`}
                      >
                        {item.data.text || item.text}
                      </span>
                      {(item.data.description || item.description) && (
                        <span class="visa-navigation-mobile-menu-item-description">
                          {item.data.description || item.description}
                        </span>
                      )}
                    </div>
                  </div>
                  {item.hasChildren && (
                    <span class="visa-navigation-mobile-menu-item-chevron">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                      >
                        <path
                          d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {this.renderMobileCard()}
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render hamburger menu button for mobile
   */
  private renderHamburgerButton() {
    if (!this.isMobile) {
      return null;
    }

    return (
      <button
        class="visa-navigation-hamburger-button"
        onClick={this.toggleMobileMenu}
        aria-label="Menu"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M1.33301 4H14.6663V5.33333H1.33301V4Z" fill="#1A1A1A" />
          <path d="M1.33301 10.6667H14.6663V12H1.33301V10.6667Z" fill="#1A1A1A" />
        </svg>
      </button>
    );
  }

  render() {
    if (!this.headerData) {
      return <div class="visa-navigation-loading">Loading...</div>;
    }

    // Determine position based on headerPosition prop and sticky state
    let position = '';
    if (this.isSticky) {
      position = 'fixed';
    } else {
      position = this.headerPosition === 'blended' ? 'absolute' : 'relative';
    }

    // Determine if submenu is open to apply background color
    const hasOpenSubmenu = this.activeSubmenuIndex !== -1 || this.searchOpen;

    return (
      <Host
        class={this.theme === 'dark' ? 'visa-navigation-theme-dark' : 'visa-navigation-theme-light'}
      >
        <div
          id="visa-navigation-header-height"
          style={{ display: `${!this.isSticky ? 'none' : 'block'}` }}
        ></div>
        <nav
          class={{
            'visa-navigation-navigation': true,
            'visa-navigation-mobile': this.isMobile,
            'visa-navigation-theme-dark': this.theme === 'dark',
            'visa-navigation-theme-light': this.theme === 'light',
            'visa-navigation-sticky': this.isSticky,
            'visa-navigation-menu-open': hasOpenSubmenu,
            'visa-navigation-pos-relative': position === 'relative',
            'visa-navigation-pos-absolute': position === 'absolute',
          }}
          style={{
            position,
            top: this.isSticky ? '0' : undefined,
            zIndex: this.isSticky ? '10000' : undefined,
          }}
        >
          {this.renderLogo()}

          {!this.isMobile && this.renderTopLevelTabs()}

          <div class="visa-navigation-secondary-nav-container">
            {this.renderSecondaryNav()}
            {this.renderHamburgerButton()}
          </div>

          {this.renderSearchOverlay()}

          {/* Desktop-only hover zone covering full navigation width */}
          {!this.isMobile && (
            <div
              class="visa-navigation-hover-zone"
              onMouseEnter={this.onHoverZoneEnter}
              onMouseLeave={this.onHoverZoneLeave}
            />
          )}

          {/* Add overlay when submenu is open */}
          {this.activeSubmenuIndex !== -1 && (
            <div
              class="visa-navigation-submenu-overlay"
              onMouseOver={this.closeActiveSubmenu}
            ></div>
          )}

          {/* Mobile menu */}
          {this.renderMobileMenu()}
        </nav>
      </Host>
    );
  }
}
